import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../store/hooks";
import { selectCreditConfigs, selectData } from "../formComponentSlicer";
import { CreditLine } from "../../dtos/CreditConfig";

export interface CreditInputProps {
  title: string;
  stitchTypeId: string;
  onChange: (value: string, id: string) => void;
  creditConfigId: string;
}

const CreditInput: React.FC<CreditInputProps> = ({
  title,
  stitchTypeId,
  onChange,
  creditConfigId,
}) => {
  console.log("Rendering CreditInput for stitchTypeId:", stitchTypeId);
  //Style Helpers
  const getDefaultMargin = () => {
    return { margin: "15px 0" };
  };

  const getColor = () => {
    return "white";
  };

  const getBorderColor = () => {
    return "white";
  };

  const getSelectedColor = (value: string) => {
    if (value === selectedLineId) {
      return "#10B981"; // Tailwind's emerald-500
    }
    return "black";
  };

  const getErrorColor = () => {
    return "red";
  };

  // Styles mapped from Tailwind to inline React styles
  const styles: { [key: string]: React.CSSProperties } = {
    container: { width: "100%", display: "flex", flexDirection: "column" },
    rowBetween: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      width: "100%",
    },
    relativeFullCol: {
      position: "relative",
      width: "100%",
      display: "flex",
      flexDirection: "column",
    },
    inputBase: {
      backgroundColor: "transparent",
      borderWidth: 1,
      borderStyle: "solid",
      borderRadius: 8,
      textAlign: "center",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      paddingTop: 8,
      paddingBottom: 8,
    },
    inputSmall: { minWidth: "190px", height: 38, cursor: "pointer" },
    absoluteRightTop: {
      position: "absolute",
      right: 12,
      top: 8,
      cursor: "pointer",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    smallBorderDiv: {
      borderWidth: 1,
      borderStyle: "solid",
      height: 20,
      marginRight: 12,
    },
    selectList: {
      position: "absolute",
      marginTop: 40,
      minWidth: "190px",
      borderWidth: 1,
      borderStyle: "solid",
      borderRadius: 12,
      boxShadow: "0 10px 15px rgba(0,0,0,0.08)",
      textAlign: "start",
      maxHeight: 200,
      overflow: "auto",
      backgroundColor: "#ffffff",
    },
    optionItem: {
      paddingLeft: 16,
      paddingRight: 16,
      paddingTop: 8,
      paddingBottom: 8,
      cursor: "pointer",
      listStyleType: "none",
    },
    flexColStartMt4: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      alignItems: "flex-start",
      marginTop: "4px",
    },
    headerTitle: {
      fontSize: 15,
      lineHeight: "23px",
      fontWeight: "bold",
      alignSelf: "flex-start",
      textAlign: "left",
      marginTop: "4px",
      marginBottom: "4px",
    },
    headerSmallCentered: {
      fontSize: 13,
      lineHeight: "23px",
      fontWeight: "bold",
      alignSelf: "center",
      textAlign: "left",
      marginTop: "4px",
      marginBottom: "0px",
    },
  };

  const numberToCOP = (num: number): string => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const calculateMonthlyPayment = (
    principal: number,
    annualRatePercent: number,
    termMonths: number
  ): number => {
    if (annualRatePercent === 0) return principal / termMonths;
    const monthlyRate = annualRatePercent / 100 / 12;
    return (
      (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -termMonths))
    );
  };

  //State
  const creditConfigs = useAppSelector(selectCreditConfigs);
  const data = useAppSelector(selectData);
  const amountStr = data[`${stitchTypeId}.amount`];
  const amount = Number.parseInt(amountStr || "0", 10);
  const termMonthsStr = data[`${stitchTypeId}.termMonths`];
  const termMonths = Number.parseInt(termMonthsStr || "0", 10);
  const creditConfig = creditConfigs[creditConfigId];
  const selectedLineId = data[`${stitchTypeId}.lineId`];
  // const [amount, setAmount] = useState<number>(0);
  const [selectedLine, setSelectedLine] = useState<CreditLine | undefined>(
    undefined
  );
  const [calculatedRate, setCalculatedRate] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [amountErrorMessage, setAmountErrorMessage] = useState<string>("");
  const [termErrorMessage, setTermErrorMessage] = useState<string>("");
  const [rateErrorMessage, setRateErrorMessage] = useState<string>("");
  const paymentAmntStr = data[`${stitchTypeId}.payment`];
  const paymentAmnt = Number.parseFloat(paymentAmntStr || "0");
  // Effect Hooks
  useEffect(() => {
    const config = creditConfig?.lines.find(
      (line) => line.lineId === selectedLineId
    );
    setSelectedLine(config);
  }, [selectedLineId, creditConfig]);
  useEffect(() => {
    if (selectedLine) {
      const rate = selectedLine.rates.find(
        (r) =>
          amount >= r.lowerBoundAmount &&
          amount <= r.upperBoundAmount &&
          termMonths >= r.lowerBoundTermMonths &&
          termMonths <= r.upperBoundTermMonths
      );
      if (rate) {
        setCalculatedRate(rate ? rate.annualInterestRate : 0);
        setRateErrorMessage("");
      } else {
        setCalculatedRate(0);
        setRateErrorMessage(
          "No hay una tasa disponible para el monto y plazo seleccionados"
        );
      }
    }
  }, [amount, termMonths, selectedLine]);

  useEffect(() => {
    let errorAmount = "";
    if (selectedLine) {
      if (amount < selectedLine.minAmount) {
        errorAmount = `El monto mínimo es de ${numberToCOP(
          selectedLine.minAmount
        )}`;
      } else if (amount > selectedLine.maxAmount) {
        errorAmount = `El monto máximo es de ${numberToCOP(
          selectedLine.maxAmount
        )}`;
      }
    }
    setAmountErrorMessage(errorAmount);
  }, [amount, selectedLine]);

  useEffect(() => {
    let errorTerm = "";
    if (selectedLine) {
      if (termMonths < selectedLine.minTermMonths) {
        errorTerm = `El plazo mínimo es de ${selectedLine.minTermMonths} meses`;
      } else if (termMonths > selectedLine.maxTermMonths) {
        errorTerm = `El plazo máximo es de ${selectedLine.maxTermMonths} meses`;
      }
    }
    setTermErrorMessage(errorTerm);
  }, [termMonths, selectedLine]);

  useEffect(() => {
    if (!amountErrorMessage && !termErrorMessage && !rateErrorMessage) {
      onChange("valid", `${stitchTypeId}`);
      let payment = calculateMonthlyPayment(amount, calculatedRate, termMonths);
      let pmntString = payment.toFixed(2);
      onChange(pmntString, `${stitchTypeId}.payment`);
      onChange((calculatedRate*100).toFixed(2) + "%", `${stitchTypeId}.rateText`);
      onChange(calculatedRate.toString(), `${stitchTypeId}.rate`);
    } else {
      onChange("", `${stitchTypeId}`);
      onChange("0", `${stitchTypeId}.payment`);
      onChange("", `${stitchTypeId}.rateText`);
      onChange("", `${stitchTypeId}.rate`);
    }
  }, [
    creditConfigId,
    amount,
    termMonths,
    amountErrorMessage,
    termErrorMessage,
    rateErrorMessage,
  ]);

  // Handlers
  const handleSelectOptionClick = (option: CreditLine) => {
    setIsOpen(false);
    onChange(option.lineId, `${stitchTypeId}.lineId`);
    onChange(option.name, `${stitchTypeId}.lineName`);
  };

  const handleTermChange = (value: number) => {
    onChange(value.toString(), `${stitchTypeId}.termMonths`);
  };

  const handleAmountChange = (value: number) => {
    onChange(value.toString(), `${stitchTypeId}.amount`);
  };

    const sleep = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const close = async () => {
    await sleep(150);
    setIsOpen(false);
  };
  // Render
  return (
    <div style={{ ...styles.container, ...getDefaultMargin() }}>
      <div style={styles.rowBetween}>
        <h6 style={{ ...styles.headerTitle, color: getColor() }}>
          {title || "Selecciona tu Línea de Crédito"}
        </h6>
      </div>
      <div style={{ ...styles.rowBetween, width: "100%" }}>
        <div style={styles.relativeFullCol}>
          <input
            id="lineaCredito"
            type="text"
            value={selectedLine?.name ? selectedLine.name : ""}
            readOnly
            onClick={() => setIsOpen((prev) => !prev)}
            onBlur={() => close()}
            placeholder={"LÍNEA DE CRÉDITO"}
            style={{
              ...styles.inputBase,
              ...styles.inputSmall,
              padding: 0,
              color: getColor(),
              borderColor: getBorderColor(),
            }}
          />
          <div
            style={styles.absoluteRightTop}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div
              style={{
                ...styles.smallBorderDiv,
                borderColor: getBorderColor(),
              }}
            ></div>
            <svg
              width="20"
              height="16"
              viewBox="0 0 20 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.90735e-06 3.33882C0.00308228 2.80387 0.116661 2.27562 0.333303 1.78865C0.560415 1.26198 0.931032 0.813264 1.4009 0.496092C1.87076 0.17892 2.42001 0.00669988 2.98305 0L17.015 0C17.578 0.00669988 18.1273 0.17892 18.5972 0.496092C19.067 0.813264 19.4376 1.26198 19.6648 1.78865C19.9448 2.39465 20.0526 3.06888 19.976 3.73449C19.8994 4.4001 19.6414 5.03032 19.2315 5.55335L12.2155 14.2411C11.9501 14.5818 11.6131 14.8569 11.2293 15.046C10.8456 15.2351 10.425 15.3333 9.99903 15.3333C9.57302 15.3333 9.15249 15.2351 8.76874 15.046C8.385 14.8569 8.04791 14.5818 7.78258 14.2411L0.766592 5.55335C0.269764 4.92711 -0.000940323 4.1451 1.90735e-06 3.33882Z"
                fill={getColor()}
              />
            </svg>
          </div>
          {isOpen && (
            <ul
              style={{
                ...(styles.selectList as React.CSSProperties),
                borderColor: getBorderColor(),
                zIndex: 1000,
              }}
            >
              {[...(creditConfig?.lines || [])].map((option, index) => (
                <li
                  key={option.lineId}
                  style={{
                    ...styles.optionItem,
                    color: getSelectedColor(option.lineId),
                  }}
                  onClick={() => handleSelectOptionClick(option)}
                >
                  {option.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {selectedLine && (
        <div>
          {/* Plazo */}
          <div style={styles.flexColStartMt4}>
            <h6
              style={{
                ...styles.headerTitle,
                color: getColor(),
                marginTop: "14px",
              }}
            >
              {"Plazo"}
            </h6>
            <input
              style={{
                ...styles.inputBase,
                color: getColor(),
                borderColor: getBorderColor(),
              }}
              type="text"
              inputMode="numeric"
              value={termMonths}
              onChange={(e) => {
                let num = Number.parseInt(e.target.value, 10);
                if (Number.isNaN(num)) {
                  num = 0;
                }
                handleTermChange(num);
              }}
            />
            {termErrorMessage && (
              <h6
                style={{
                  ...styles.headerSmallCentered,
                  color: getErrorColor(),
                }}
              >
                {termErrorMessage}
              </h6>
            )}
          </div>
          {/* Monto */}
          <div style={styles.flexColStartMt4}>
            <h6 style={{ ...styles.headerTitle, color: getColor() }}>
              {"Monto"}
            </h6>
            <input
              style={{
                ...styles.inputBase,
                color: getColor(),
                borderColor: getBorderColor(),
              }}
              type="text"
              inputMode="numeric"
              value={
                amount !== null && amount !== undefined && amount !== 0
                  ? new Intl.NumberFormat("es-CO", {
                      style: "currency",
                      currency: "COP",
                      maximumFractionDigits: 0,
                    }).format(amount)
                  : ""
              }
              onChange={(e) => {
                let digits = e.target.value.replaceAll(/\D+/g, "");
                let newAmount = digits ? Number.parseInt(digits, 10) : 0;
                handleAmountChange(newAmount);
              }}
            />
            {amountErrorMessage && (
              <h6
                style={{
                  ...styles.headerSmallCentered,
                  color: getErrorColor(),
                }}
              >
                {amountErrorMessage}
              </h6>
            )}
          </div>
          {/* Tasa */}
          <div style={styles.flexColStartMt4}>
            <h6 style={{ ...styles.headerTitle, color: getColor() }}>
              {"Tasa de Interés Efectiva Anual (%)"}
            </h6>
            <input
              style={{
                ...styles.inputBase,
                color: getColor(),
                borderColor: getBorderColor(),
              }}
              type="text"
              disabled={true}
              value={`${(calculatedRate*100).toFixed(2)}%`}
            />
            {rateErrorMessage && (
              <h6
                style={{
                  ...styles.headerSmallCentered,
                  color: getErrorColor(),
                }}
              >
                {rateErrorMessage}
              </h6>
            )}
          </div>
          {/* Cuota  */}
          {creditConfig.showPayment && (
            <div style={styles.flexColStartMt4}>
              <h6 style={{ ...styles.headerTitle, color: getColor() }}>
                {"Cuota Mensual"}
              </h6>
              <input
                style={{
                  ...styles.inputBase,
                  color: getColor(),
                  borderColor: getBorderColor(),
                }}
                type="text"
                disabled={true}
                value={`${numberToCOP(paymentAmnt)}`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CreditInput;
